import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Calendar, Mail, MapPin, Phone, User } from "lucide-react";
import { api, formatApiError } from "../../api";
import { LEADS } from "../../constants/testIds";
import { LEAD_STAGE_BY_KEY } from "../../lib/leadStages";
import { relativeTimeFromNow } from "../../lib/format";
import {
  Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle,
} from "../ui/sheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { StatusPill } from "../common/StatusPill";
import { toast } from "../ui/sonner";

function toDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// UX-CRM-03 §4/§5: "click a row -> full detail" — the SAME drawer opens from every one of the 5
// tabs, always showing every field (`PATCH /leads/{id}` already supported note/follow_up_at
// edits before this session — see routers/crm.py — so no backend change was needed here). Also
// where a lost lead gets its one recovery path: "Reopen" (-> back to new_lead), a small
// zero-friction addition per the user's own "user friction should be zero" ask.
export function LeadDetailDrawer({ lead, planById, branchNameById, onOpenChange, onConvert, onMarkLost }) {
  const queryClient = useQueryClient();
  const [followUp, setFollowUp] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (lead) { setFollowUp(toDatetimeLocal(lead.follow_up_at)); setNote(lead.note || ""); }
  }, [lead]);

  const saveMutation = useMutation({
    mutationFn: () => api.patch(`/leads/${lead.id}`, { follow_up_at: followUp ? new Date(followUp).toISOString() : null, note: note || null }),
    onSuccess: () => { toast.success("Saved."); queryClient.invalidateQueries({ queryKey: ["leads"] }); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const stageMutation = useMutation({
    mutationFn: (stage) => api.patch(`/leads/${lead.id}/stage`, { stage }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["leads"] }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  if (!lead) return null;
  const stage = LEAD_STAGE_BY_KEY[lead.stage];
  const isConverted = lead.stage === "converted";
  const isLost = lead.stage === "lost";

  return (
    <Sheet open={!!lead} onOpenChange={onOpenChange}>
      <SheetContent data-testid={LEADS.detailDrawer} className="overflow-y-auto w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{lead.contact.name}</SheetTitle>
          <SheetDescription>Captured {relativeTimeFromNow(lead.created_at)} · Last touched {relativeTimeFromNow(lead.updated_at)}</SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-4">
          <StatusPill status="active" label={isConverted ? "Converted" : isLost ? "Lost" : stage?.label || lead.stage} />

          <div className="space-y-2 text-sm">
            <p className="flex items-center gap-2" style={{ color: "#141416" }}><Phone size={13} style={{ color: "#68686E" }} /> {lead.contact.phone}</p>
            {lead.contact.email && <p className="flex items-center gap-2" style={{ color: "#141416" }}><Mail size={13} style={{ color: "#68686E" }} /> {lead.contact.email}</p>}
            {branchNameById?.[lead.branch_id] && <p className="flex items-center gap-2" style={{ color: "#141416" }}><MapPin size={13} style={{ color: "#68686E" }} /> {branchNameById[lead.branch_id]}</p>}
            {lead.assigned_to_name && <p className="flex items-center gap-2" style={{ color: "#141416" }}><User size={13} style={{ color: "#68686E" }} /> Captured by {lead.assigned_to_name}</p>}
            <p style={{ color: "#68686E" }}>Source: <span style={{ color: "#141416" }}>{lead.source.replace(/_/g, " ")}</span></p>
            {lead.plan_interest && planById?.[lead.plan_interest] && <p style={{ color: "#68686E" }}>Interested in: <span style={{ color: "#141416" }}>{planById[lead.plan_interest].name}</span></p>}
          </div>

          {!isConverted && !isLost && (
            <div className="space-y-1.5">
              <Label>Stage</Label>
              <select
                data-testid={LEADS.detailStageSelect} value={lead.stage} onChange={(e) => stageMutation.mutate(e.target.value)}
                className="w-full rounded-[10px] px-3 py-2 text-sm" style={{ border: "1px solid #EBEAE5" }}
              >
                <option value="new_lead">New Lead</option>
                <option value="contacted">Contacted</option>
                <option value="interested">Interested</option>
                <option value="demo_trial">Demo / Trial</option>
                <option value="negotiation">Negotiation</option>
              </select>
            </div>
          )}

          {!isConverted && (
            <>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5"><Calendar size={12} /> Next contact date & time</Label>
                <Input data-testid={LEADS.detailFollowUpInput} type="datetime-local" value={followUp} onChange={(e) => setFollowUp(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Comment</Label>
                <Textarea data-testid={LEADS.detailNoteInput} value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="What's the latest on this lead?" />
              </div>
              <Button data-testid={LEADS.detailSaveButton} variant="outline" className="w-full" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
                {saveMutation.isPending ? "Saving…" : "Save"}
              </Button>
            </>
          )}
        </div>

        {!isConverted && (
          <SheetFooter className="mt-6 flex-col gap-2 sm:flex-col">
            {isLost ? (
              <Button data-testid={LEADS.detailReopenButton} className="w-full" variant="outline" onClick={() => stageMutation.mutate("new_lead")}>Reopen lead</Button>
            ) : (
              <>
                <Button data-testid={LEADS.detailConvertButton} className="w-full text-white" style={{ background: "#D6202B" }} onClick={onConvert}>Convert to member</Button>
                <button data-testid={LEADS.detailMarkLostButton} onClick={onMarkLost} className="text-xs hover:underline" style={{ color: "#A0A0A5" }}>Mark as lost</button>
              </>
            )}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}
